/**
 * Three.js Scene Constants
 * 
 * Shared constants for consistent 3D scene rendering across all game types.
 * These values are calibrated for accurate pixel-to-3D unit conversion
 * when using Angular Three with distanceFactor: 0 (pixel-perfect mode).
 */

/**
 * Pixel-to-3D Unit Conversion
 * 
 * Base calibration formula: (pixels / CALIBRATION_DIVISOR) * BASE_MULTIPLIER * DIAMETER_FACTOR
 * Example: (768px / 768) * 9.5 * 2 = 19 units
 */
export const PIXEL_TO_UNIT = {
  /** Calibration divisor for pixel-perfect mode (matches medium wheel size) */
  CALIBRATION_DIVISOR: 768,
  
  /** Base multiplier for 3D unit conversion */
  BASE_MULTIPLIER: 9.5,
  
  /** Diameter factor (2x for full width/height vs radius) */
  DIAMETER_FACTOR: 2,
} as const;

/**
 * Empirical Corrections
 * 
 * Fine-tuning adjustments discovered through visual testing across
 * multiple viewport sizes and game types.
 */
export const EMPIRICAL_CORRECTIONS = {
  /**
   * HTML Visual Extent Correction (1%)
   * 
   * Testing across all sizes shows consistent ~1% undershoot in the
   * pixel-perfect formula. This correction ensures 3D dimensions match
   * the actual rendered HTML visual extent.
   * 
   * Possible causes:
   * - Sub-pixel rendering differences between HTML and WebGL canvases
   * - Minor variance in calibration constants
   * - Angular Three's internal CSS transform adjustments
   */
  HTML_SIZE_MULTIPLIER: 1.01,
  
  /** Game board border margin (3% larger than HTML content) */
  BOARD_BORDER_MARGIN: 1.03,
  
  /** Wheel cylinder border (5% larger than HTML wheel) */
  WHEEL_BORDER_MARGIN: 1.05,
} as const;

/**
 * Three-Point Lighting Configuration
 * 
 * Professional lighting setup used across all scene components for
 * consistent depth, shadows, and visual quality.
 */
export const THREE_POINT_LIGHTING = {
  /** Ambient light - base illumination */
  AMBIENT: {
    intensity: 0.8,
  },
  
  /** Hemisphere light - natural sky/ground gradient */
  HEMISPHERE: {
    skyColor: 0xffffff,     // White (16777215)
    groundColor: 0x444444,   // Blue-grey (4473924)
    intensity: 0.5,
  },
  
  /** Key light - main directional (strongest, casts shadows) */
  KEY_LIGHT: {
    position: [5, 8, 5] as [number, number, number],
    intensity: 2.5,
    castShadow: true,
  },
  
  /** Fill light - softer secondary (reduces harsh shadows) */
  FILL_LIGHT: {
    position: [-3, 4, -2] as [number, number, number],
    intensity: 0.8,
    castShadow: false,
  },
  
  /** Rim light - back-highlight (adds depth and separation) */
  RIM_LIGHT: {
    position: [0, 3, -6] as [number, number, number],
    intensity: 1.0,
    castShadow: false,
  },
} as const;

/**
 * Reflective Overlay Material Properties
 * 
 * Optimized material settings for glass-like reflective overlays
 * that enhance visual quality without blocking UI interactions.
 */
export const REFLECTIVE_OVERLAY = {
  /** Game board reflective layer (subtle, with separate shadow layer) */
  BOARD: {
    opacity: 0.1,
    roughness: 0.0,
    metalness: 1.0,
    blending: 'additive' as const,
  },
  
  /** Wheel reflective layer (more visible on circular surface) */
  WHEEL: {
    opacity: 0.2,
    roughness: 0.0,
    metalness: 1.0,
    blending: 'additive' as const,
  },
  
  /** Scratchcard reflective layer (matches wheel) */
  SCRATCHCARD: {
    opacity: 0.2,
    roughness: 0.0,
    metalness: 1.0,
    blending: 'additive' as const,
  },
  
  /** Shadow layer (game board only) */
  SHADOW: {
    opacity: 0.1,
  },
} as const;
